/**
 * useModal Hook
 *
 * Core modal state management hook with responsive design detection,
 * device capabilities, and modal lifecycle management.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

interface ModalOptions {
  defaultOpen?: boolean;
  preventClose?: boolean;
  closeOnEscape?: boolean;
  closeOnOutsideClick?: boolean;
  maintainState?: boolean;
  responsive?: boolean;
  mobileVariant?: 'dialog' | 'sheet' | 'drawer';
  desktopVariant?: 'dialog' | 'sheet';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  onOpen?: () => void;
  onClose?: () => void;
  onBeforeClose?: () => Promise<boolean> | boolean;
  onAfterOpen?: () => void;
}

interface ModalState {
  isOpen: boolean;
  isClosing: boolean;
  isOpening: boolean;
  hasUnsavedChanges: boolean;
  preventClose: boolean;
  size: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  variant: 'dialog' | 'sheet' | 'drawer';
  historyState?: any;
}

interface ModalReturn {
  // State
  isOpen: boolean;
  isClosing: boolean;
  isOpening: boolean;
  hasUnsavedChanges: boolean;
  preventClose: boolean;
  size: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  variant: 'dialog' | 'sheet' | 'drawer';
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;

  // Actions
  open: () => void;
  close: () => void;
  toggle: () => void;
  preventClosing: (prevent: boolean) => void;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  setSize: (size: ModalState['size']) => void;
  setVariant: (variant: ModalState['variant']) => void;

  // Utilities
  canClose: () => boolean;
  resetState: () => void;
}

const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;

const DEFAULT_OPTIONS: Required<Omit<ModalOptions, 'defaultOpen' | 'onOpen' | 'onClose' | 'onBeforeClose' | 'onAfterOpen'>> = {
  preventClose: false,
  closeOnEscape: true,
  closeOnOutsideClick: true,
  maintainState: false,
  responsive: true,
  mobileVariant: 'drawer',
  desktopVariant: 'dialog',
  size: 'lg',
};

/**
 * Hook for managing modal state with responsive design and lifecycle management.
 */
export function useModal(options: ModalOptions = {}): ModalReturn {
  const config = { ...DEFAULT_OPTIONS, ...options };

  // Device detection
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);

  // Modal state
  const [state, setState] = useState<ModalState>({
    isOpen: config.defaultOpen || false,
    isClosing: false,
    isOpening: false,
    hasUnsavedChanges: false,
    preventClose: config.preventClose,
    size: config.size,
    variant: config.desktopVariant,
  });

  // Refs for cleanup and state tracking
  const previousStateRef = useRef(state);
  const closeTimeoutRef = useRef<NodeJS.Timeout>();
  const openTimeoutRef = useRef<NodeJS.Timeout>();
  const historyEntryRef = useRef<string | null>(null);

  // Responsive breakpoint detection
  useEffect(() => {
    if (!config.responsive) return;

    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < MOBILE_BREAKPOINT);
      setIsTablet(width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT);
      setIsDesktop(width >= TABLET_BREAKPOINT);
    };

    // Initial check
    checkDevice();

    // Add resize listener with debouncing
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkDevice, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [config.responsive]);

  // Auto-variant selection based on device
  useEffect(() => {
    if (!config.responsive) return;

    const newVariant = isMobile
      ? config.mobileVariant
      : config.desktopVariant;

    setState(prev => ({
      ...prev,
      variant: prev.variant !== newVariant ? newVariant : prev.variant,
    }));
  }, [isMobile, config.mobileVariant, config.desktopVariant, config.responsive]);

  // Open modal
  const open = useCallback(() => {
    if (state.isOpen) return;

    setState(prev => ({
      ...prev,
      isOpening: true,
      isClosing: false,
    }));

    // Add history entry for proper back navigation
    if (typeof window !== 'undefined' && window.history) {
      historyEntryRef.current = `modal-${Date.now()}`;
      window.history.pushState(
        { modalOpen: true, modalId: historyEntryRef.current },
        '',
        window.location.pathname
      );
    }

    // Trigger open callback
    config.onOpen?.();

    // Animation timing
    openTimeoutRef.current = setTimeout(() => {
      setState(prev => ({
        ...prev,
        isOpen: true,
        isOpening: false,
      }));
      config.onAfterOpen?.();
    }, 50);

    previousStateRef.current = state;
  }, [state.isOpen, config]);

  // Close modal with confirmation for unsaved changes
  const close = useCallback(async (force = false) => {
    if (!state.isOpen || state.isClosing) return;

    // Check for unsaved changes
    if (!force && state.hasUnsavedChanges) {
      const confirmed = window.confirm(
        'Você tem alterações não salvas. Tem certeza que deseja fechar?'
      );
      if (!confirmed) return;
    }

    // Run before close hook
    if (config.onBeforeClose) {
      const canClose = await config.onBeforeClose();
      if (!canClose) return;
    }

    setState(prev => ({
      ...prev,
      isClosing: true,
      isOpening: false,
    }));

    // Remove history entry
    if (typeof window !== 'undefined' && window.history && historyEntryRef.current) {
      window.history.back();
      historyEntryRef.current = null;
    }

    // Animation timing
    closeTimeoutRef.current = setTimeout(() => {
      setState(prev => ({
        ...prev,
        isOpen: false,
        isClosing: false,
        // Reset state if not maintaining
        hasUnsavedChanges: config.maintainState ? prev.hasUnsavedChanges : false,
      }));
      config.onClose?.();
    }, 300);

    previousStateRef.current = state;
  }, [state.isOpen, state.isClosing, state.hasUnsavedChanges, config]);

  // Toggle modal
  const toggle = useCallback(() => {
    if (state.isOpen) {
      close();
    } else {
      open();
    }
  }, [state.isOpen, close, open]);

  // Prevent/allow closing
  const preventClosing = useCallback((prevent: boolean) => {
    setState(prev => ({
      ...prev,
      preventClose: prevent,
    }));
  }, []);

  // Set unsaved changes state
  const setHasUnsavedChanges = useCallback((hasChanges: boolean) => {
    setState(prev => ({
      ...prev,
      hasUnsavedChanges: hasChanges,
    }));
  }, []);

  // Set modal size
  const setSize = useCallback((size: ModalState['size']) => {
    setState(prev => ({
      ...prev,
      size: size,
    }));
  }, []);

  // Set modal variant
  const setVariant = useCallback((variant: ModalState['variant']) => {
    setState(prev => ({
      ...prev,
      variant: variant,
    }));
  }, []);

  // Check if modal can be closed
  const canClose = useCallback(() => {
    return !state.preventClose && state.isOpen && !state.isClosing;
  }, [state.preventClose, state.isOpen, state.isClosing]);

  // Reset modal state
  const resetState = useCallback(() => {
    clearTimeout(openTimeoutRef.current);
    clearTimeout(closeTimeoutRef.current);

    setState({
      isOpen: false,
      isClosing: false,
      isOpening: false,
      hasUnsavedChanges: false,
      preventClose: config.preventClose,
      size: config.size,
      variant: config.desktopVariant,
      historyState: undefined,
    });

    if (historyEntryRef.current) {
      historyEntryRef.current = null;
    }
  }, [config.preventClose, config.size, config.desktopVariant]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (event.state?.modalOpen && state.isOpen) {
        close(true); // Force close on back navigation
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [state.isOpen, close]);

  // Handle escape key (global handler for preventClose)
  useEffect(() => {
    if (!config.closeOnEscape || !state.isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && canClose()) {
        event.preventDefault();
        close();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [config.closeOnEscape, state.isOpen, canClose, close]);

  // Cleanup timeouts
  useEffect(() => {
    return () => {
      clearTimeout(openTimeoutRef.current);
      clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  // Show warning for unsaved changes on page unload
  useEffect(() => {
    if (!state.hasUnsavedChanges) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = 'Você tem alterações não salvas. Tem certeza que deseja sair?';
      return event.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.hasUnsavedChanges]);

  return {
    // State
    isOpen: state.isOpen,
    isClosing: state.isClosing,
    isOpening: state.isOpening,
    hasUnsavedChanges: state.hasUnsavedChanges,
    preventClose: state.preventClose,
    size: state.size,
    variant: state.variant,
    isMobile,
    isTablet,
    isDesktop,

    // Actions
    open,
    close,
    toggle,
    preventClosing,
    setHasUnsavedChanges,
    setSize,
    setVariant,

    // Utilities
    canClose,
    resetState,
  };
}

/**
 * Hook for managing global modal state and stacking.
 * Useful for applications that need to handle multiple modals.
 */
export function useModalStack() {
  const [stack, setStack] = useState<string[]>([]);
  const [activeModal, setActiveModal] = useState<string | null>(null);

  const pushModal = useCallback((modalId: string) => {
    setStack(prev => [...prev, modalId]);
    setActiveModal(modalId);
  }, []);

  const popModal = useCallback(() => {
    setStack(prev => {
      const newStack = prev.slice(0, -1);
      setActiveModal(newStack.length > 0 ? newStack[newStack.length - 1] : null);
      return newStack;
    });
  }, []);

  const removeModal = useCallback((modalId: string) => {
    setStack(prev => {
      const newStack = prev.filter(id => id !== modalId);
      setActiveModal(newStack.length > 0 ? newStack[newStack.length - 1] : null);
      return newStack;
    });
  }, []);

  const clearStack = useCallback(() => {
    setStack([]);
    setActiveModal(null);
  }, []);

  const isTopModal = useCallback((modalId: string) => {
    return activeModal === modalId;
  }, [activeModal]);

  return {
    stack,
    activeModal,
    pushModal,
    popModal,
    removeModal,
    clearStack,
    isTopModal,
    stackSize: stack.length,
  };
}

export type { ModalOptions, ModalState, ModalReturn };